import React from 'react';
import { motion } from 'framer-motion';

interface BackgroundAnimationProps {
  variant?: 'floating' | 'gradient' | 'particles' | 'waves';
  intensity?: 'subtle' | 'medium' | 'intense';
}

const BackgroundAnimation: React.FC<BackgroundAnimationProps> = ({ 
  variant = 'floating', 
  intensity = 'subtle' 
}) => {
  const getIntensityValue = () => {
    switch (intensity) {
      case 'subtle': return { opacity: 0.1, scale: 0.8 };
      case 'medium': return { opacity: 0.2, scale: 1 };
      case 'intense': return { opacity: 0.3, scale: 1.2 };
      default: return { opacity: 0.1, scale: 0.8 };
    }
  };

  const intensityValue = getIntensityValue();

  if (variant === 'floating') {
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating geometric shapes */}
        <motion.div
          className="absolute top-20 left-10 w-20 h-20 bg-gradient-to-r from-primary-400/20 to-purple-400/20 rounded-full blur-xl"
          animate={{
            x: [0, 30, 0],
            y: [0, -20, 0],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          style={{ opacity: intensityValue.opacity }}
        />
        
        <motion.div
          className="absolute top-40 right-20 w-16 h-16 bg-gradient-to-r from-cyan-400/20 to-blue-400/20 rounded-full blur-xl"
          animate={{
            x: [0, -25, 0],
            y: [0, 30, 0],
            rotate: [0, -180, -360],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear",
            delay: 2
          }}
          style={{ opacity: intensityValue.opacity }}
        />
        
        <motion.div
          className="absolute bottom-32 left-1/4 w-24 h-24 bg-gradient-to-r from-pink-400/20 to-rose-400/20 rounded-full blur-xl"
          animate={{
            x: [0, 40, 0],
            y: [0, -15, 0],
            rotate: [0, 90, 180],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "linear",
            delay: 5
          }}
          style={{ opacity: intensityValue.opacity }}
        />
        
        <motion.div
          className="absolute bottom-20 right-1/3 w-12 h-12 bg-gradient-to-r from-emerald-400/20 to-teal-400/20 rounded-full blur-xl"
          animate={{
            x: [0, -20, 0],
            y: [0, 25, 0],
            rotate: [0, -90, -180],
          }}
          transition={{
            duration: 22,
            repeat: Infinity,
            ease: "linear",
            delay: 8
          }}
          style={{ opacity: intensityValue.opacity }}
        />
      </div>
    );
  }

  if (variant === 'particles') {
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-primary-400/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0, 1, 0],
              scale: [0, 1, 0],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 3,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>
    );
  }

  if (variant === 'waves') {
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-primary-400/5 via-purple-400/5 to-cyan-400/5"
          animate={{
            backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        
        <svg className="absolute bottom-0 w-full h-32" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <motion.path
            d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
            fill="url(#wave-gradient)"
            opacity={intensityValue.opacity}
            animate={{
              d: [
                "M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z",
                "M0,0V30.29c47.79,15.2,103.59,22.17,158,18,70.36-3.37,136.33-23.31,206.8-27.5C438.64,22.43,512.34,33.67,583,42.05c69.27,12,138.3,14.88,209.4,8.08,36.15-4,69.85-12.84,104.45-19.34C989.49,15,1113-9.29,1200,32.47V0Z",
                "M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
              ]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <defs>
            <linearGradient id="wave-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgb(99, 102, 241)" stopOpacity="0.1" />
              <stop offset="50%" stopColor="rgb(147, 51, 234)" stopOpacity="0.1" />
              <stop offset="100%" stopColor="rgb(6, 182, 212)" stopOpacity="0.1" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    );
  }

  if (variant === 'gradient') {
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-primary-400/10 via-purple-400/10 to-cyan-400/10"
          animate={{
            background: [
              "linear-gradient(45deg, rgba(99, 102, 241, 0.1) 0%, rgba(147, 51, 234, 0.1) 50%, rgba(6, 182, 212, 0.1) 100%)",
              "linear-gradient(135deg, rgba(6, 182, 212, 0.1) 0%, rgba(99, 102, 241, 0.1) 50%, rgba(147, 51, 234, 0.1) 100%)",
              "linear-gradient(225deg, rgba(147, 51, 234, 0.1) 0%, rgba(6, 182, 212, 0.1) 50%, rgba(99, 102, 241, 0.1) 100%)",
              "linear-gradient(45deg, rgba(99, 102, 241, 0.1) 0%, rgba(147, 51, 234, 0.1) 50%, rgba(6, 182, 212, 0.1) 100%)"
            ]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      </div>
    );
  }

  return null;
};

export default BackgroundAnimation; 